import { typeToString } from '@/util/type-to-string'
import type { Module } from 'vuex'
import type { StoreState } from '../store-state'

/**
 * 예약시트의 화면 설정 — 병원별로 기억한다.
 *
 * 서버가 주는 그룹·담당자·예약상태 목록과 별개로, 이 브라우저에서 정한 순서(order)와
 * 켜고 끈 상태(isSelected)만 남긴다. 시트는 서버 목록을 받은 뒤 여기 기록을 덮어씌운다.
 */
export type StoredSheetItem = {
    _id: string
    order?: number
    isSelected?: boolean
    groupID?: string
}

export type StoredHospitalSheet = {
    timeInterval?: number
    groups?: StoredSheetItem[]
    columns?: StoredSheetItem[]
    states?: StoredSheetItem[]
}

export type V2ReservationState = {
    hospitals: Record<string, StoredHospitalSheet | undefined>
}

const state: V2ReservationState = {
    hospitals: {},
}

/** 그 병원 칸을 꺼낸다. 없으면 만들어 둔다 */
function hospitalOf(state: V2ReservationState, hospitalID: string): StoredHospitalSheet {
    if (state.hospitals[hospitalID] == undefined) { state.hospitals[hospitalID] = {} }
    return state.hospitals[hospitalID] as StoredHospitalSheet
}

/** 기억할 네 가지만 추린다 */
const pick = (item: StoredSheetItem): StoredSheetItem => ({
    _id: item._id,
    order: item.order,
    isSelected: item.isSelected,
    groupID: item.groupID,
})

const mutations = {
    'MU_V2Reservation_SetTimeInterval': (
        state: V2ReservationState,
        param: { hospitalID: string; timeInterval: number },
    ): void => {
        hospitalOf(state, param.hospitalID).timeInterval = param.timeInterval
    },

    'MU_V2Reservation_UpdateGroups': (
        state: V2ReservationState,
        param: { hospitalID: string; groups: StoredSheetItem[] },
    ): void => {
        // 병합 루프가 있었지만 마지막에 통째로 덮어써 무의미했음 — 덮어쓰기만 유지 (동작 동일)
        hospitalOf(state, param.hospitalID).groups = param.groups
    },

    'MU_V2Reservation_SetGroups': (
        state: V2ReservationState,
        param: { hospitalID: string; groups: StoredSheetItem[] },
    ): void => {
        hospitalOf(state, param.hospitalID).groups = param.groups.map(pick)
    },

    /** 있으면 갈아끼우고 없으면 더한다. groupID 가 채워져 오면 _id 만 남긴다 */
    'MU_V2Reservation_UpdateColumns': (
        state: V2ReservationState,
        param: { hospitalID: string; columns: (Omit<StoredSheetItem, 'groupID'> & { groupID?: string | { _id: string } })[] },
    ): void => {
        const hospital = hospitalOf(state, param.hospitalID)
        if (hospital.columns == undefined) { hospital.columns = [] }
        const columns = hospital.columns

        param.columns.forEach((newOne) => {
            const parsed = pick({ ...newOne, groupID: undefined })
            parsed.groupID = typeToString(newOne.groupID) == 'object'
                ? (newOne.groupID as { _id: string })._id
                : (newOne.groupID as string | undefined)
            const target = columns.find((c) => c._id == newOne._id)
            if (target != undefined) { columns.splice(columns.indexOf(target), 1, parsed) }
            else { columns.push(parsed) }
        })
    },

    'MU_V2Reservation_SetColumns': (
        state: V2ReservationState,
        param: { hospitalID: string; columns: StoredSheetItem[] },
    ): void => {
        const hospital = hospitalOf(state, param.hospitalID)
        if (hospital.columns == undefined) { hospital.columns = [] }
        hospital.columns = param.columns
    },

    'MU_V2Reservation_SetStates': (
        state: V2ReservationState,
        param: { hospitalID: string; states: StoredSheetItem[] },
    ): void => {
        hospitalOf(state, param.hospitalID).states = param.states
    },
}

const V2Reservation: Module<V2ReservationState, StoreState> = { state, mutations }

export default V2Reservation
